import type { ReactNode } from 'react';

import { ApiFailure } from './api';

/**
 * Состояния экрана со списком (T11.3, `09_UI_UX.md` §6).
 *
 * Пять обязательных: загрузка, пусто, ошибка сети, нет связи, упор в
 * лимит тарифа. Каждое — не украшение, а ответ на вопрос «что мне
 * делать»: экран без ответа человек закрывает и не возвращается.
 *
 * ## Скелет, а не спиннер
 *
 * Спиннер сообщает «ждите», скелет — «вот что появится». Разница в том,
 * что глаз успевает выбрать, куда смотреть, до прихода данных, и
 * появление ощущается быстрее при той же задержке.
 *
 * ## Отказы различаются по причине, а не по виду
 *
 * «Что-то пошло не так» одинаково для сети, отказа сервера и упора в
 * лимит, а действия у них разные: повторить, подождать, заплатить.
 * Одинаковое сообщение отнимает возможность выбрать верное.
 */

/** Полоса скелета: место, которое займут данные. */
function Bar({ width }: { width: string }): React.JSX.Element {
  return <span className="skeleton__bar" style={{ width }} />;
}

/**
 * Загрузка списка.
 *
 * Строк столько же, сколько обычно приходит в первом экране: скелет
 * короче настоящего списка дёргает страницу вверх при появлении данных.
 */
export function ListSkeleton({ rows = 4 }: { rows?: number }): React.JSX.Element {
  return (
    <div className="skeleton" aria-busy="true" aria-label="Загрузка">
      {Array.from({ length: rows }, (_, at) => (
        <div className="card skeleton__row" key={at}>
          <Bar width="62%" />
          <Bar width="38%" />
          <Bar width="80%" />
        </div>
      ))}
    </div>
  );
}

export interface EmptyProps {
  readonly title: string;
  /** Что сделать, чтобы стало непусто. Без этого экран — тупик. */
  readonly hint: string;
  readonly action?: { readonly label: string; readonly onClick: () => void };
}

/**
 * Пустой результат.
 *
 * Подсказка обязательна: «ничего не найдено» без продолжения оставляет
 * человека гадать, сломался сервис или он сам виноват.
 */
export function EmptyState({ title, hint, action }: EmptyProps): React.JSX.Element {
  return (
    <div className="state">
      <p className="state__title">{title}</p>
      <p className="state__hint">{hint}</p>
      {action === undefined ? null : (
        <button type="button" className="button" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
}

/**
 * Отказ с повтором.
 *
 * Кнопка повтора есть всегда: отказ сети проходит сам, и заставлять
 * человека закрывать и открывать приложение ради этого — грубость.
 */
export function ErrorState({
  title,
  hint,
  onRetry,
}: {
  title: string;
  hint: string;
  onRetry: () => void;
}): React.JSX.Element {
  return (
    <div className="state">
      <p className="state__title">{title}</p>
      <p className="state__hint">{hint}</p>
      <button type="button" className="button" onClick={onRetry}>
        Повторить
      </button>
    </div>
  );
}

/**
 * Упор в лимит тарифа.
 *
 * Отдельно от прочих отказов: человек не сделал ничего плохого, и
 * действие у него не «повторить», а «расширить». Экран оплаты
 * открывается в момент упора, а не заранее (`09_UI_UX.md` §8).
 */
export function LimitState({
  title,
  hint,
  onUpgrade,
}: {
  title: string;
  hint: string;
  onUpgrade: () => void;
}): React.JSX.Element {
  return (
    <div className="state">
      <p className="state__title">{title}</p>
      <p className="state__hint">{hint}</p>
      <button type="button" className="button button--accent" onClick={onUpgrade}>
        Подключить Pro
      </button>
    </div>
  );
}

/** Коды, означающие упор в тариф, а не поломку. */
export const LIMIT_CODES: ReadonlySet<string> = new Set([
  'SUBSCRIPTION_LIMIT_REACHED',
  'QUOTA_EXCEEDED',
  'PLAN_REQUIRED',
  'FORBIDDEN_PLAN',
]);

/**
 * Что показать вместо списка.
 *
 * Разбор идёт от причины к действию, а не от кода к тексту. Отсутствие
 * связи проверяется первым: при выключенной сети сервер не отвечал
 * вовсе, и коду отказа взяться неоткуда.
 */
export function renderFailure(
  error: unknown,
  handlers: { onRetry: () => void; onUpgrade: () => void },
): ReactNode {
  // Браузер сообщает об отсутствии сети сам, и это точнее любых догадок
  // по коду отказа
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return (
      <ErrorState
        title="Нет соединения"
        hint="Похоже, интернет пропал. Проверьте связь и попробуйте снова."
        onRetry={handlers.onRetry}
      />
    );
  }

  // Вход не состоялся. Повторять бессмысленно: строку входа Telegram
  // выдаёт на открытие приложения, и та, что у нас на руках, уже не
  // годится — просрочена или израсходована. Лекарство одно, и назвать
  // его надо прямо, иначе человек будет жать «повторить» до раздражения
  if (error instanceof ApiFailure && error.code === 'UNAUTHORIZED') {
    return (
      <ErrorState
        title="Нужно открыть заново"
        hint="Закройте приложение и откройте его снова из чата — Telegram выдаст новый пропуск."
        onRetry={handlers.onRetry}
      />
    );
  }

  if (error instanceof ApiFailure && LIMIT_CODES.has(error.code)) {
    return (
      <LimitState
        title="Достигнут предел тарифа"
        hint={error.message}
        onUpgrade={handlers.onUpgrade}
      />
    );
  }

  if (error instanceof ApiFailure) {
    return (
      <ErrorState title="Не удалось загрузить" hint={error.message} onRetry={handlers.onRetry} />
    );
  }

  // Не отказ API: оборванное соединение, отменённый запрос, сбой
  // разбора. Человеку из этого важно одно — можно попробовать ещё раз
  return (
    <ErrorState
      title="Не удалось загрузить"
      hint="Сервис не ответил. Это бывает; попробуйте ещё раз."
      onRetry={handlers.onRetry}
    />
  );
}
